/* =========================================================
   Relewise slider — arrow navigation + shadow-DOM tile sizing
   Wires the prev/next buttons in .relewise-slider-section to
   the Relewise recommendation web component (which is itself
   the horizontally scrollable container, per the CSS overrides
   in section-relewise-slider.css).

   The product tiles render INSIDE the recommendation component's
   shadow root, so outer CSS can't size them. We inject a stylesheet
   into the shadow root so each tile gets a fixed flex-basis and
   the slider actually overflows when there are more than 5.5 cards.

   The Relewise widget renders asynchronously after fetching
   recommendations, so we use a MutationObserver to attach the
   scroll listener and inject tile styles once the host appears.
   ========================================================= */

(function () {
  // Guard against double execution — each section liquid includes a
  // <script> tag so the file ran twice with 2 sections present.
  if (window.__rwSliderInitialized) return;
  window.__rwSliderInitialized = true;

  // Debug surface — readable from console as window.__rwSlider
  window.__rwSlider = {
    iifeRanAt: Date.now(),
    initCalls: 0,
    setupCalls: 0,
    injectAttempts: 0,
    injectSuccesses: 0,
    injectShadowRootMissing: 0,
    pollTicks: 0,
    observerFires: 0,
    sections: [],
  };
  var debug = window.__rwSlider;

  // Build a single Constructable Stylesheet to share across all hosts.
  // adoptedStyleSheets is the right tool here because Lit-based web
  // components (which Relewise uses) re-render their shadow DOM on every
  // data change and remove any <style> element that isn't part of the
  // template. Sheets in adoptedStyleSheets are NOT in the rendered DOM,
  // so Lit's reconciliation can't touch them — they survive re-renders.
  var sharedSheet = null;
  function getSheet() {
    if (sharedSheet) return sharedSheet;
    if (typeof CSSStyleSheet === 'undefined' || !CSSStyleSheet.prototype.replaceSync) {
      return null; // unsupported, fall back to <style>
    }
    sharedSheet = new CSSStyleSheet();
    sharedSheet.replaceSync(TILE_SHADOW_CSS);
    return sharedSheet;
  }

  var SCROLLER_SELECTOR = [
    'relewise-products-viewed-after-viewing-product',
    'relewise-recently-viewed-products',
    'relewise-popular-products',
    'relewise-purchased-with-product',
    'relewise-personal-products',
    'relewise-products-viewed-after-viewing-content',
  ].join(',');

  // Stylesheet injected into the recommendation widget's shadow root so
  // <relewise-product-tile> children get a fixed flex basis. CSS custom
  // properties inherit through shadow boundaries, so var(--s-3) and the
  // --rw-cards-* / --rw-tile-min-width tokens resolve to whatever the
  // outer .relewise-slider-section wrapper sets — defaults below match
  // the original 5.5 / 3.5 / 2.2 layout, and a wrapper can override the
  // tokens (e.g. .relewise-slider-section--wide) to vary one slider
  // without changing the others.
  // !important is required because the Relewise tile component sets its
  // own :host styles inside its own shadow tree, which can otherwise win
  // the cascade against this rule.
  var TILE_SHADOW_CSS = [
    'relewise-product-tile {',
    /* Desktop: clamp the basis so the formula provides an IDEAL width for
       the target visible count, but tiles never shrink below
       --rw-tile-min-width (slider then overflows horizontally and scrolls)
       and never grow past --rw-tile-max-width (caps card size on very
       wide monitors). flex: 0 0 disables grow/shrink so the clamped
       basis IS the final width. */
    '  flex: 0 0 clamp(',
    '    var(--rw-tile-min-width, 14rem),',
    '    calc((100% - (var(--rw-cards-desktop, 5.5) - 1) * var(--s-1, 4px)) / var(--rw-cards-desktop, 5.5)),',
    '    var(--rw-tile-max-width, 100rem)',
    '  ) !important;',
    /* Flex items default to min-width: auto (= their min-content width),
       a hard floor that overrides the basis above. A busy card (e.g. one
       with many no-wrap colour swatches) then has a wider min-content than
       the basis and the tile GROWS to fit it — which, because the image
       height is width-derived, makes that one card's image taller. This
       only shows in the tablet band, where the basis clamps down to
       --rw-tile-min-width and sits right at the swatch row's min-content.
       min-width: 0 removes the floor so every tile is exactly the clamped
       basis → uniform widths → uniform image heights at every viewport. */
    '  min-width: 0 !important;',
    '  scroll-snap-align: start;',
    '}',
    '@media (max-width: 989px) {',
    '  relewise-product-tile {',
    '    flex: 0 0 clamp(',
    '      var(--rw-tile-min-width, 14rem),',
    '      calc((100% - (var(--rw-cards-tablet, 3.5) - 1) * var(--s-1, 4px)) / var(--rw-cards-tablet, 3.5)),',
    '      var(--rw-tile-max-width, 100rem)',
    '    ) !important;',
    '  }',
    '}',
    '@media (max-width: 749px) {',
    '  relewise-product-tile {',
    '    flex: 0 0 clamp(',
    '      var(--rw-tile-min-width, 14rem),',
    '      calc((100% - (var(--rw-cards-mobile, 2.2) - 1) * var(--s-1, 4px)) / var(--rw-cards-mobile, 2.2)),',
    '      var(--rw-tile-max-width, 100rem)',
    '    ) !important;',
    '  }',
    '}',
  ].join('\n');

  function injectTileStyles(host) {
    debug.injectAttempts++;
    if (!host || !host.shadowRoot) {
      debug.injectShadowRootMissing++;
      return false;
    }
    var sr = host.shadowRoot;

    // Preferred path: adopt a Constructable Stylesheet. This survives
    // Lit re-renders because it's not part of the rendered DOM.
    var sheet = getSheet();
    if (sheet) {
      var current = sr.adoptedStyleSheets || [];
      // Skip if already adopted by this host.
      for (var i = 0; i < current.length; i++) {
        if (current[i] === sheet) {
          debug.injectSuccesses++;
          return true;
        }
      }
      sr.adoptedStyleSheets = current.concat([sheet]);
      debug.injectSuccesses++;
      return true;
    }

    // Fallback for old browsers: <style> element. This will be wiped by
    // Lit's next re-render, but we re-inject on every poll tick to cover
    // for that. (Modern Chrome/Firefox/Safari take the path above.)
    if (sr.querySelector('style[data-rw-tile-styles]')) return true;
    var style = document.createElement('style');
    style.setAttribute('data-rw-tile-styles', '');
    style.textContent = TILE_SHADOW_CSS;
    sr.appendChild(style);
    debug.injectSuccesses++;
    return true;
  }

  function init(section) {
    debug.initCalls++;
    var sectionDebug = { hasPrev: false, hasNext: false, hasScroller: null };
    debug.sections.push(sectionDebug);
    var prev = section.querySelector('[data-rw-slider-prev]');
    var next = section.querySelector('[data-rw-slider-next]');
    sectionDebug.hasPrev = !!prev;
    sectionDebug.hasNext = !!next;
    if (!prev || !next) return;

    var scrollAttached = false;
    var stylesInjected = false;

    function getScroller() {
      return section.querySelector(SCROLLER_SELECTOR);
    }

    function getScrollAmount(scroller) {
      // Move exactly one tile + the flex gap per click.
      var tile = scroller.shadowRoot && scroller.shadowRoot.querySelector('relewise-product-tile');
      if (tile) {
        var cs = getComputedStyle(scroller);
        var gap = parseFloat(cs.columnGap) || parseFloat(cs.gap) || 0;
        return tile.offsetWidth + gap;
      }
      // Fallback if the tile isn't measurable yet.
      return Math.max(200, Math.round(scroller.clientWidth * 0.2));
    }

    function updateArrowStates() {
      var scroller = getScroller();
      if (!scroller) return;
      var maxScroll = scroller.scrollWidth - scroller.clientWidth;
      // If layout hasn't settled yet (e.g. measured before paint), be
      // lenient: leave next enabled so the user can still try clicking
      // and aren't stuck behind a stale "no overflow" reading.
      if (maxScroll <= 0) {
        prev.disabled = true;
        next.disabled = false;
        return;
      }
      prev.disabled = scroller.scrollLeft <= 1;
      next.disabled = scroller.scrollLeft >= maxScroll - 1;
    }

    function setup() {
      debug.setupCalls++;
      var scroller = getScroller();
      sectionDebug.hasScroller = !!scroller;
      if (!scroller) return false;

      if (!stylesInjected) {
        stylesInjected = injectTileStyles(scroller);
      }

      if (stylesInjected && !scrollAttached) {
        scroller.addEventListener('scroll', updateArrowStates, { passive: true });
        scrollAttached = true;
        // Defer the first measurement to after the next paint so the
        // adopted stylesheet has fully applied and overflow is real.
        requestAnimationFrame(updateArrowStates);
        // Also recheck once images and other late assets finish loading.
        window.addEventListener('load', updateArrowStates, { once: true });
      }

      return stylesInjected && scrollAttached;
    }

    prev.addEventListener('click', function () {
      var scroller = getScroller();
      if (!scroller) return;
      scroller.scrollBy({ left: -getScrollAmount(scroller), behavior: 'smooth' });
    });

    next.addEventListener('click', function () {
      var scroller = getScroller();
      if (!scroller) return;
      scroller.scrollBy({ left: getScrollAmount(scroller), behavior: 'smooth' });
    });

    // Initial start: arrows disabled until the widget renders.
    prev.disabled = true;
    next.disabled = true;

    // Try immediately — the widget may already be there.
    if (setup()) {
      window.addEventListener('resize', updateArrowStates, { passive: true });
      return;
    }

    // Persistent polling: Relewise attaches its shadow root only after the
    // recommendations API resolves, which can take 5–10s. Shadow DOM
    // mutations don't bubble to outer MutationObservers, so polling is
    // the only reliable signal that the shadow root is now accessible.
    // Poll every 200ms for up to 30s; stop the moment injection succeeds.
    var pollAttempts = 0;
    var pollInterval = setInterval(function () {
      debug.pollTicks++;
      pollAttempts += 1;
      if (setup() || pollAttempts >= 150) {
        clearInterval(pollInterval);
      }
    }, 200);

    // Also observe light-DOM changes — covers the case where the host
    // element itself is added late (e.g. by a script that injects the
    // app block container post-load).
    var observer = new MutationObserver(function () {
      debug.observerFires++;
      if (setup()) observer.disconnect();
    });
    observer.observe(section, { childList: true, subtree: true });

    // Recalculate on resize since visible card count changes by breakpoint.
    window.addEventListener('resize', updateArrowStates, { passive: true });
  }

  function initAll() {
    document.querySelectorAll('.relewise-slider-section').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
